import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, IconButton, Chip, Tooltip, TextField, MenuItem, Button,
  Dialog, Grid, CircularProgress, useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import swal from "sweetalert";
import api from "../components/utils/api";
import NuevoReporteServicio from "./NuevoReporteServicio";
import { generarPdfReporte } from "../components/utils/generarPdfReporte";
import dayjs from "dayjs";

function fmtFecha(val) {
  if (!val) return "-";
  return dayjs(val).format("DD/MM/YYYY");
}

function EstadoBadge({ estado }) {
  const es = (estado || "").toLowerCase();
  if (es === "finalizado") {
    return <Chip label="Finalizado" size="small" sx={{ bgcolor: "#2E7D32", color: "#fff", fontWeight: 600, fontSize: "0.7rem" }} />;
  }
  return <Chip label="En curso" size="small" sx={{ bgcolor: "#1565C0", color: "#fff", fontWeight: 600, fontSize: "0.7rem" }} />;
}

function KpiCard({ label, value, color }) {
  return (
    <Paper elevation={2} sx={{ p: 2, borderRadius: 2, borderLeft: "4px solid " + color, display: "flex", flexDirection: "column", minWidth: 130, flex: 1 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>{label}</Typography>
      <Typography variant="h5" fontWeight={700} color={color} mt={0.5}>{value}</Typography>
    </Paper>
  );
}

export default function ReporteServicio() {
  const theme      = useTheme();
  const isMobile   = useMediaQuery(theme.breakpoints.down("sm"));
  const userToken  = localStorage.getItem("user-token");
  const rol        = localStorage.getItem("roles");
  const esAdmin    = (rol || "").toLowerCase().includes("admin");
  const headers    = { "iron-token": userToken, "Content-Type": "application/json" };

  const [servicios, setServicios]       = useState([]);
  const [loading, setLoading]           = useState(false);
  const [searchRaw, setSearchRaw]       = useState("");
  const [search, setSearch]             = useState("");
  const [tipoFiltro, setTipoFiltro]     = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState("");
  const [modalOpen, setModalOpen]       = useState(false);
  const [editData, setEditData]         = useState({});
  const [modalMode, setModalMode]       = useState("create");

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchRaw), 300);
    return () => clearTimeout(t);
  }, [searchRaw]);

  const listar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(api.reporteServicio, { headers });
      if (!res.ok) throw new Error("No se pudieron obtener los registros.");
      setServicios(await res.json());
    } catch (err) {
      swal("Error", err.message, "error");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { listar(); }, [listar]);

  const kpis = useMemo(() => {
    const total       = servicios.length;
    const finalizados = servicios.filter(s => (s.estado || "").toLowerCase() === "finalizado").length;
    const enCurso     = total - finalizados;
    const esteMes     = servicios.filter(s => {
      if (!s.fecha) return false;
      const f = dayjs(s.fecha);
      return f.month() === dayjs().month() && f.year() === dayjs().year();
    }).length;
    return { total, finalizados, enCurso, esteMes };
  }, [servicios]);

  const tiposUnicos = useMemo(() => [...new Set(servicios.map(s => s.tipo).filter(Boolean))], [servicios]);

  const filtrados = useMemo(() => {
    const q = search.toLowerCase();
    return servicios.filter(s => {
      const ok1 = !q || [s.cliente, s.serie, s.ubicacion, s.modelo, String(s.id)].some(v => (v || "").toLowerCase().includes(q));
      const ok2 = !tipoFiltro   || s.tipo === tipoFiltro;
      const ok3 = !estadoFiltro || (s.estado || "").toLowerCase() === estadoFiltro.toLowerCase();
      return ok1 && ok2 && ok3;
    });
  }, [servicios, search, tipoFiltro, estadoFiltro]);

  const abrirModal = useCallback((row, mode) => {
    setEditData(row); setModalMode(mode); setModalOpen(true);
  }, []);

  const eliminar = useCallback(async (row) => {
    const ok = await swal({ title: "Confirmar", text: "Se eliminara el registro permanentemente.", icon: "warning", buttons: ["Cancelar", "Eliminar"], dangerMode: true });
    if (!ok) return;
    if (row.flagCrm === "1") { swal("Error", "No se pueden eliminar registros informados al CRM.", "error"); return; }
    try {
      setLoading(true);
      const res = await fetch(api.reporteServicio + row.id, { method: "DELETE", headers });
      if (!res.ok) throw new Error("Error al eliminar");
      swal("Listo", "El registro se elimino correctamente.", "success");
      listar();
    } catch (err) { swal("Error", err.message, "error"); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listar]);

  const finalizar = useCallback(async (row) => {
    const ok = await swal({ title: "Finalizar orden?", text: "Esta accion finalizara la orden permanentemente.", icon: "warning", buttons: ["Cancelar", "Finalizar"], dangerMode: true });
    if (!ok) return;
    try {
      setLoading(true);
      const res = await fetch(api.reporteServicio + "estado/" + row.id, { method: "PUT", headers });
      if (!res.ok) throw new Error("Error al finalizar");
      swal("Listo", "El registro se finalizo correctamente.", "success");
      listar();
    } catch (err) { swal("Error", err.message, "error"); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listar]);

  const limpiar = () => { setSearchRaw(""); setTipoFiltro(""); setEstadoFiltro(""); };

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 1400, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Typography variant="h5" fontWeight={700}>Reportes de servicio</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => abrirModal({}, "create")}
          sx={{ bgcolor: "#ED9519", "&:hover": { bgcolor: "#c97d0e" }, fontWeight: 700 }}>
          Nuevo reporte
        </Button>
      </Box>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <KpiCard label="Total"       value={kpis.total}       color="#ED9519" />
        <KpiCard label="Finalizados" value={kpis.finalizados} color="#2E7D32" />
        <KpiCard label="En curso"    value={kpis.enCurso}     color="#1565C0" />
        <KpiCard label="Este mes"    value={kpis.esteMes}     color="#7B1FA2" />
      </Box>

      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField label="Buscar cliente, serie, ubicacion..." size="small" fullWidth value={searchRaw} onChange={e => setSearchRaw(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField label="Tipo" select size="small" fullWidth value={tipoFiltro} onChange={e => setTipoFiltro(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              {tiposUnicos.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField label="Estado" select size="small" fullWidth value={estadoFiltro} onChange={e => setEstadoFiltro(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="finalizado">Finalizado</MenuItem>
              <MenuItem value="nuevo">En curso</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs="auto">
            <Button variant="outlined" startIcon={<ClearIcon />} onClick={limpiar} size="small">Limpiar</Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress sx={{ color: "#ED9519" }} />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ "& th": { bgcolor: "#1A1A2E", color: "#fff", fontWeight: 700 } }}>
                <TableCell>ID</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Tipo equipo</TableCell>
                <TableCell>Serie</TableCell>
                <TableCell>Ubicacion</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell align="center" sx={{ width: 160, minWidth: 160 }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtrados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No hay registros con los filtros actuales
                  </TableCell>
                </TableRow>
              ) : filtrados.map((s, i) => (
                <TableRow key={s.id} sx={{ bgcolor: i % 2 === 0 ? "transparent" : "action.hover", "&:hover": { bgcolor: "action.selected" } }}>
                  <TableCell sx={{ fontSize: "0.8rem", color: "text.secondary" }}>#{s.id}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.85rem" }}>{s.cliente}</TableCell>
                  <TableCell sx={{ fontSize: "0.82rem" }}>{s.tipo}</TableCell>
                  <TableCell sx={{ fontSize: "0.82rem", fontFamily: "monospace" }}>{s.serie || "-"}</TableCell>
                  <TableCell sx={{ fontSize: "0.82rem" }}>
                    <Tooltip title={s.ubicacion || ""} arrow>
                      <Typography variant="body2" sx={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.82rem" }}>
                        {s.ubicacion || "-"}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.82rem", whiteSpace: "nowrap" }}>{fmtFecha(s.fecha)}</TableCell>
                  <TableCell align="center"><EstadoBadge estado={s.estado} /></TableCell>
                  <TableCell align="center" sx={{ whiteSpace: "nowrap", width: 160, minWidth: 160 }}>
                    <Tooltip title="Ver">
                      <IconButton size="small" onClick={() => abrirModal(s, "view")}>
                        <RemoveRedEyeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Generar PDF">
                      <IconButton size="small" sx={{ color: "#C62828" }} onClick={() => generarPdfReporte(s)}>
                        <PictureAsPdfIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {esAdmin && (
                      <>
                        <Tooltip title="Editar">
                          <IconButton size="small" color="primary" onClick={() => abrirModal(s, "edit")}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={(s.estado || "").toLowerCase() === "finalizado" ? "Ya finalizado" : "Finalizar"}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={(s.estado || "").toLowerCase() === "finalizado"}
                              onClick={() => finalizar(s)}
                              sx={{ color: "#2E7D32", "&.Mui-disabled": { color: "action.disabled" } }}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => eliminar(s)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={modalOpen}
        onClose={() => { setModalOpen(false); listar(); }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { height: isMobile ? "100%" : "90vh" } }}
      >
        <NuevoReporteServicio
          mode={modalMode}
          initialData={editData}
          onClose={() => { setModalOpen(false); listar(); }}
        />
      </Dialog>
    </Box>
  );
}
